import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Scale, Ruler, Baby } from 'lucide-react';
import { BabyData, BabyInfo } from '@/store/pregnancyStore';
import { useTranslation } from '@parentpath/i18n';

interface EditBirthDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (birthDate: string, birthTime: string | null, babyData: BabyData) => Promise<void>;
  currentBirthDate: string | null;
  currentBirthTime: string | null;
  currentBabyData: BabyData | null;
  childCount: number;
}

export function EditBirthDataModal({
  isOpen,
  onClose,
  onSave,
  currentBirthDate,
  currentBirthTime,
  currentBabyData,
  childCount,
}: EditBirthDataModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [babies, setBabies] = useState<BabyInfo[]>([]);

  // Initialize form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setBirthDate(currentBirthDate || new Date().toISOString().split('T')[0]);
      setBirthTime(currentBirthTime || '');

      // Initialize babies from currentBabyData
      const existingBabies = currentBabyData?.babies || [];
      const initialBabies: BabyInfo[] = Array(childCount)
        .fill(null)
        .map((_, i) => ({
          name: existingBabies[i]?.name || '',
          gender: existingBabies[i]?.gender,
          weight_grams: existingBabies[i]?.weight_grams,
          height_cm: existingBabies[i]?.height_cm,
        }));
      setBabies(initialBabies);
      setError(null);
    }
  }, [isOpen, currentBirthDate, currentBirthTime, currentBabyData, childCount]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!birthDate) {
      setError(t('profile:modals.editBirthData.dateRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const babyData: BabyData = {
        babies: babies,
        // Keep top-level values from first baby for backwards compatibility
        weight_grams: babies[0]?.weight_grams,
        height_cm: babies[0]?.height_cm,
      };

      await onSave(birthDate, birthTime || null, babyData);
      onClose();
    } catch (err) {
      setError(t('profile:modals.editBirthData.saveError'));
      console.error('Error saving birth data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBaby = (index: number, field: keyof BabyInfo, value: string | number | undefined) => {
    setBabies(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleWeightChange = (index: number, value: string) => {
    const numValue = value === '' ? undefined : parseInt(value, 10);
    updateBaby(index, 'weight_grams', numValue);
  };

  const handleHeightChange = (index: number, value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    updateBaby(index, 'height_cm', numValue);
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={() => !isLoading && onClose()} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.editBirthData.title')}</h2>
            </div>
            <button
              onClick={() => !isLoading && onClose()}
              disabled={isLoading}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - scrollable */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {childCount > 1 ? t('profile:modals.editBirthData.descriptionPlural') : t('profile:modals.editBirthData.description')}
            </p>

            {/* Birth Date & Time */}
            <div className="p-3 bg-white/10 rounded-[15px] space-y-3">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('profile:modals.editBirthData.birthDate')}
                </label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-[10px] text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('profile:modals.editBirthData.birthTime')}
                </label>
                <input
                  type="time"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-[10px] text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                />
              </div>
            </div>

            {/* Baby Data */}
            {babies.map((baby, index) => (
              <div key={index} className="p-3 bg-white/10 rounded-[15px] space-y-3">
                {childCount > 1 && (
                  <div className="flex items-center gap-2 mb-2">
                    <Baby className="w-4 h-4 text-pp-orange" />
                    <span className="text-white font-medium">
                      {baby.name || `Baby ${index + 1}`}
                    </span>
                  </div>
                )}

                {/* Weight */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    <Scale className="w-3.5 h-3.5 inline mr-1.5" />
                    {t('profile:modals.editBirthData.birthWeight')}
                  </label>
                  <input
                    type="number"
                    placeholder={t('profile:modals.editBirthData.weightPlaceholder')}
                    value={baby.weight_grams || ''}
                    onChange={(e) => handleWeightChange(index, e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                    min={0}
                    max={10000}
                  />
                </div>

                {/* Height */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">
                    <Ruler className="w-3.5 h-3.5 inline mr-1.5" />
                    {t('profile:modals.editBirthData.birthHeight')}
                  </label>
                  <input
                    type="number"
                    placeholder={t('profile:modals.editBirthData.heightPlaceholder')}
                    value={baby.height_cm || ''}
                    onChange={(e) => handleHeightChange(index, e.target.value)}
                    className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                    min={0}
                    max={100}
                    step={0.1}
                  />
                </div>
              </div>
            ))}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[15px]">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Hint */}
            <p className="text-xs text-white/50">
              {t('profile:modals.editBirthData.hint')}
            </p>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={() => !isLoading && onClose()}
              disabled={isLoading}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('common:save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
